import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, Building, TrendingUp, Target, X, File } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyProfileSetup = ({ isOpen, onClose, companyId, onComplete }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState(null);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setUploading(false);
      setUploadProgress(0);
      setResult(null);
    }
  }, [isOpen]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    // Add new files to existing selection
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Selecciona al menos un archivo');
      return;
    }

    const userToken = localStorage.getItem('token');
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(
        `${API}/companies/${companyId}/company-profile/ingest`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );

      setResult(response.data);
      toast.success(`¡${selectedFiles.length} archivo(s) procesados exitosamente!`);
      
      // Reset files after successful upload
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error uploading profile:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar archivos');
    } finally {
      setUploading(false);
    }
  };

  const handleComplete = () => {
    // First notify parent that configuration is complete
    if (onComplete) {
      onComplete(result);
    }
    // Reset local state
    setResult(null);
    setSelectedFiles([]);
    // Close the modal
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-dark border-blue-500/20 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <Building className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Configuración del Perfil Empresarial</h1>
          <p className="text-slate-400 text-lg">
            Sube información sobre tu empresa para obtener análisis personalizados
          </p>
        </div>

        {!result ? (
          <Card className="glass-dark border-blue-500/20">
            <CardHeader>
              <CardTitle className="text-white">Información de la Empresa</CardTitle>
              <CardDescription className="text-slate-400">
                Sube documentos que describan tu empresa, modelo de negocio, estructura organizacional y operaciones.
                Esto nos ayudará a clasificar tu industria y personalizar los análisis.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* File Types Info */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="text-center">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                  <p className="text-sm text-slate-300">PDF</p>
                  <p className="text-xs text-slate-500">Documentos corporativos</p>
                </div>
                <div className="text-center">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-green-400" />
                  <p className="text-sm text-slate-300">Excel</p>
                  <p className="text-xs text-slate-500">Estructura organizacional</p>
                </div>
                <div className="text-center">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                  <p className="text-sm text-slate-300">CSV</p>
                  <p className="text-xs text-slate-500">Datos generales</p>
                </div>
              </div>

              {/* Upload Area */}
              <div 
                className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-blue-500 transition-colors"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                  <p className="text-slate-300 mb-2">
                    Arrastra archivos aquí o haz clic para seleccionar
                  </p>
                  <p className="text-sm text-slate-500">
                    Puedes seleccionar <strong>múltiples archivos</strong> a la vez
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    Soporta: PDF, Excel, CSV, Word, TXT
                  </p>
                </label>
              </div>

              {/* Selected Files List */}
              {selectedFiles.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-400 font-medium">
                      {selectedFiles.length} archivo(s) seleccionado(s)
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedFiles([])}
                      className="text-red-400 hover:text-red-300 text-xs"
                    >
                      Limpiar todos
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-2 pr-2">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700 group"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <File className="w-5 h-5 text-blue-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-white truncate">{file.name}</p>
                            <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => removeFile(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add more files button */}
                  <label 
                    htmlFor="file-upload" 
                    className="block w-full p-2 text-center text-sm text-blue-400 hover:text-blue-300 cursor-pointer border border-dashed border-slate-600 rounded-lg hover:border-blue-500 transition-colors"
                  >
                    + Agregar más archivos
                  </label>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Subiendo archivos...</span>
                    <span className="text-blue-400">{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-xs text-slate-500 text-center">
                    Procesando {selectedFiles.length} archivo(s) con IA...
                  </p>
                </div>
              )}

              {/* Upload Button */}
              <Button
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0}
                className="w-full btn-gradient h-12 text-lg"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Procesando y Clasificando...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Procesar {selectedFiles.length > 0 ? `${selectedFiles.length} Archivo(s)` : 'y Clasificar Empresa'}
                  </>
                )}
              </Button>

              {/* Info */}
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  <strong>💡 Tip:</strong> Sube documentos como presentaciones corporativas, organigramas,
                  descripciones de productos/servicios, o cualquier información que describa tu empresa.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="glass-dark border-green-500/20">
            <CardHeader>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <CardTitle className="text-white">Clasificación Completada</CardTitle>
                  <CardDescription className="text-slate-400">
                    Tu empresa ha sido clasificada exitosamente
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Classification */}
              <div className="p-6 bg-gradient-to-br from-blue-900/20 to-cyan-900/20 rounded-lg border border-blue-500/30">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Clasificación de Industria
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Industria Principal</p>
                    <p className="text-white font-medium">
                      {result.classification?.primary_industry || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Industria Secundaria</p>
                    <p className="text-white font-medium">
                      {result.classification?.secondary_industry || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Sub-Industria</p>
                    <p className="text-white font-medium">
                      {result.classification?.sub_industry || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Modelo de Negocio</p>
                    <p className="text-white font-medium">
                      {result.classification?.business_model || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 text-center">
                  <TrendingUp className="w-6 h-6 mx-auto mb-2 text-blue-400" />
                  <p className="text-2xl font-bold text-white">{result.confidence_score}%</p>
                  <p className="text-xs text-slate-400">Confianza</p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 text-center">
                  <Target className="w-6 h-6 mx-auto mb-2 text-purple-400" />
                  <p className="text-lg font-bold text-white">
                    {result.maturity_level === 'Growing' ? 'Crecimiento' : 
                     result.maturity_level === 'Early' ? 'Inicial' :
                     result.maturity_level === 'Scaled' ? 'Escalado' :
                     result.maturity_level === 'Enterprise' ? 'Empresa' :
                     result.maturity_level}
                  </p>
                  <p className="text-xs text-slate-400">Madurez</p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 text-center">
                  <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-400" />
                  <p className="text-lg font-bold text-white">
                    {result.is_valid ? 'Válido' : 'Revisar'}
                  </p>
                  <p className="text-xs text-slate-400">Estado</p>
                </div>
              </div>

              {/* Recommended Modules */}
              {result.recommended_modules && result.recommended_modules.length > 0 && (
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h4 className="text-sm font-semibold text-white mb-3">Módulos Recomendados</h4>
                  <div className="flex flex-wrap gap-2">
                    {result.recommended_modules.map((module, idx) => (
                      <Badge key={idx} className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                        {module}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {result.warnings && result.warnings.length > 0 && (
                <div className="p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-yellow-300 mb-2">Advertencias</p>
                      {result.warnings.map((warning, idx) => (
                        <p key={idx} className="text-sm text-yellow-200 mb-1">• {warning}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Complete Button */}
              <Button
                onClick={handleComplete}
                className="w-full btn-gradient h-12 text-lg"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Completar Configuración
              </Button>
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompanyProfileSetup;
